import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button, Paper, Chip, Tooltip, IconButton, Alert } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserService, UserInfo, PermissionsRequest } from '../../services/userService';
import { DemoAccountService } from '../../services/demoAccountService';
import { UserPermissionsDialog, CreateUserDialog } from './index';
import ConfirmationPopover from '../../components/ConfirmationPopover/ConfirmationPopover';
import { useConfirmationPopover } from '../../hooks/useConfirmationPopover';
import { ToastService } from '../../services/toastService';

export const UsersPage: React.FC = () => {
  const [selectedUser, setSelectedUser] = useState<UserInfo | null>(null);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserInfo | null>(null);

  // Confirmation popover for delete user
  const { anchorEl, isOpen, openConfirmation, closeConfirmation } = useConfirmationPopover();

  const queryClient = useQueryClient();

  // Fetch users
  const {
    data: usersData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['users'],
    queryFn: UserService.getAllUsers,
  });

  // Fetch demo accounts to show demo badge
  const { data: demoAccountsData } = useQuery({
    queryKey: ['demoAccounts'],
    queryFn: DemoAccountService.getDemoAccounts,
  });

  const demoUserUids = useMemo(
    () => new Set((demoAccountsData?.demoAccounts || []).map((a) => a.userUid)),
    [demoAccountsData]
  );

  // Handle query errors with toast notifications
  useEffect(() => {
    if (error) {
      console.error('Failed to load users:', error);
      ToastService.handleApiError(error, 'Loading Users');
    }
  }, [error]);

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: UserService.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      ToastService.success('User created successfully', 'User Created');
      setCreateDialogOpen(false);
    },
    onError: (error: unknown) => {
      console.error('Failed to create user:', error);
      ToastService.handleApiError(error, 'User Creation');
    },
  });

  // Update permissions mutation
  const updatePermissionsMutation = useMutation({
    mutationFn: ({ userID, permissions }: { userID: string; permissions: PermissionsRequest }) =>
      UserService.updateUserPermissions(userID, permissions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      ToastService.success('User permissions updated successfully', 'Permissions Updated');
      setPermissionsDialogOpen(false);
    },
    onError: (error: unknown) => {
      console.error('Failed to update user permissions:', error);
      ToastService.handleApiError(error, 'Permission Update');
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (userID: string) => UserService.deleteUser(userID),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      ToastService.success('User deleted successfully', 'User Deleted');
    },
    onError: (error: unknown) => {
      console.error('Failed to delete user:', error);
      ToastService.handleApiError(error, 'User Deletion');
    },
  });

  const handleEditPermissions = (user: UserInfo) => {
    setSelectedUser(user);
    setPermissionsDialogOpen(true);
  };

  const handleDeleteUser = (user: UserInfo, event?: React.MouseEvent<HTMLElement>) => {
    if (event && event.currentTarget) {
      setUserToDelete(user);
      openConfirmation(event.currentTarget);
    }
  };

  const confirmDeleteUser = () => {
    if (userToDelete) {
      deleteUserMutation.mutate(userToDelete.uid);
      setUserToDelete(null);
    }
    closeConfirmation();
  };

  const handleSavePermissions = (permissions: PermissionsRequest) => {
    if (selectedUser) {
      updatePermissionsMutation.mutate({ userID: selectedUser.uid, permissions });
    }
  };

  const handleCreateUser = (payload: Parameters<typeof UserService.createUser>[0]) => {
    createUserMutation.mutate(payload);
  };

  const getUserRoleChip = (user: UserInfo) => {
    const role = UserService.getUserRole(user);
    const hasStructured = UserService.hasStructuredPermissions(user);
    const isAdmin = UserService.isAdmin(user);

    let color: 'primary' | 'secondary' | 'default' | 'error' | 'warning' = 'default';
    let label = role;

    if (role === 'fleetManager') {
      color = 'primary';
      label = hasStructured ? 'Fleet Manager' : 'Fleet Manager (Legacy)';
    } else if (role === 'trader') {
      color = 'secondary';
      label = hasStructured ? 'Trader' : 'Trader (Legacy)';
    } else {
      color = 'error';
      label = 'No Role';
    }

    const isDemo = demoUserUids.has(user.uid);

    // If user is admin or demo, show chips alongside role chip
    if (isAdmin || isDemo) {
      return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {isAdmin && <Chip label="Admin" color="warning" size="small" variant="filled" />}
          {isDemo && <Chip label="Demo" color="info" size="small" variant="filled" />}
          <Chip
            label={label}
            color={color}
            size="small"
            variant={hasStructured ? 'filled' : 'outlined'}
          />
        </Box>
      );
    }

    return (
      <Chip
        label={label}
        color={color}
        size="small"
        variant={hasStructured ? 'filled' : 'outlined'}
      />
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'displayName',
      headerName: 'Display Name',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => UserService.formatDisplayName(params.row),
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => getUserRoleChip(params.row),
    },
    {
      field: 'lastSignIn',
      headerName: 'Last Sign In',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString();
      },
    },
    {
      field: 'disabled',
      headerName: 'Status',
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Disabled' : 'Active'}
          color={params.value ? 'error' : 'success'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit Permissions">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleEditPermissions(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete User">
            <IconButton color="error" size="small" onClick={(e) => handleDeleteUser(params.row, e)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load users: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
        <Button variant="contained" onClick={() => refetch()} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>
            New User
          </Button>
        </Box>
      </Box>

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={usersData?.users || []}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.uid}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          disableRowSelectionOnClick
          sx={{ border: 0 }}
        />
      </Paper>

      {/* Create User Dialog */}
      <CreateUserDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreate={handleCreateUser}
      />

      {/* User Permissions Dialog */}
      <UserPermissionsDialog
        open={permissionsDialogOpen}
        onClose={() => setPermissionsDialogOpen(false)}
        user={selectedUser}
        onSave={handleSavePermissions}
      />

      {/* Delete User Confirmation Popover */}
      <ConfirmationPopover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closeConfirmation}
        onConfirm={confirmDeleteUser}
        title="Delete User"
        message={`Are you sure you want to delete ${userToDelete ? UserService.formatDisplayName(userToDelete) : 'this user'}? This action cannot be undone and will permanently remove the user account.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="warning"
        isLoading={deleteUserMutation.isPending}
      />
    </Box>
  );
};
